import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

export const supportedLanguages = ["is", "en"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const defaultLanguage: SupportedLanguage = "is";
export const fallbackLanguage: SupportedLanguage = "en";
export const languageStorageKey = "ovinnufaerni.language";

export const languageOptions: Array<{
  code: SupportedLanguage;
  label: string;
}> = [
  { code: "is", label: "Íslenska" },
  { code: "en", label: "English" },
];

const isSupportedLanguage = (
  value: string | null,
): value is SupportedLanguage =>
  supportedLanguages.includes(value as SupportedLanguage);

export const getInitialLanguage = (): SupportedLanguage => {
  if (typeof window === "undefined") {
    return defaultLanguage;
  }

  const storedLanguage = window.localStorage.getItem(languageStorageKey);
  return isSupportedLanguage(storedLanguage) ? storedLanguage : defaultLanguage;
};

const isTranslations = {
  Users: "Notendur",
  "Invite user": "Bjóða notanda",
  "Login is by invitation only. Invite staff and choose their role.":
    "Innskráning er einungis með boði. Bjóddu starfsfólki og veldu hlutverk þess.",
  "They will receive an email to set their password.":
    "Viðkomandi fær tölvupóst til að velja sér lykilorð.",
  "Send invite": "Senda boð",
  "Sending...": "Sendi...",
  "Email is required": "Netfang er nauðsynlegt",
  Role: "Hlutverk",
  Secretary: "Ritari",
  Manager: "Stjórnandi",
  Admin: "Kerfisstjóri",
  Invited: "Boðið",
  Disabled: "Óvirkur",
  Enable: "Virkja",
  Disable: "Gera óvirkan",
  "Last login": "Síðasta innskráning",
  You: "Þú",
  "Resend invite": "Senda boð aftur",
  "Cancel invite": "Afturkalla boð",
  "Invite cancelled.": "Boð afturkallað.",
  "Sign-in": "Innskráning",
  "Email & password": "Netfang og lykilorð",
  "User invited. They can now sign in with Google.":
    "Notanda boðið. Viðkomandi getur nú skráð sig inn með Google.",
  "They sign in with Google using this email address. No email is sent; let them know they have access.":
    "Viðkomandi skráir sig inn með Google á þessu netfangi. Enginn tölvupóstur er sendur; láttu viðkomandi vita að aðgangur sé kominn.",
  "Invitation sent.": "Boð sent.",
  "Invitation re-sent.": "Boð sent aftur.",
  "User added. They already have an account and can log in.":
    "Notanda bætt við. Viðkomandi er þegar með aðgang og getur skráð sig inn.",
  "Role updated.": "Hlutverk uppfært.",
  "User disabled.": "Notandi gerður óvirkur.",
  "User enabled.": "Notandi virkjaður.",
  "Loading users...": "Sæki notendur...",
  "Only admins can manage users.":
    "Einungis kerfisstjórar geta haft umsjón með notendum.",
  "No access": "Enginn aðgangur",
  "Access is by invitation only. Ask an administrator to invite you.":
    "Aðgangur er einungis með boði. Biddu kerfisstjóra um að bjóða þér.",
  "Confirm deactivation": "Staðfesta óvirkjun",
  "This record will remain in lists and reports, but will no longer be available when creating episodes. You can reactivate it later.":
    "Þessi færsla verður áfram sýnileg í listum og skýrslum en verður ekki tiltæk við skráningu fjarvista. Þú getur virkjað hana aftur síðar.",
  Deactivated: "Óvirkt",
  Reactivate: "Virkja aftur",
  "Reactivate company": "Virkja fyrirtæki aftur",
  "Reactivate employee": "Virkja starfsmann aftur",
  "Reactivating...": "Virkja aftur...",
  "Company reactivated.": "Fyrirtæki virkjað aftur.",
  "Employee reactivated.": "Starfsmaður virkjaður aftur.",
  "Workstation reactivated.": "Starfsstöð virkjuð aftur.",
  "This employee is deactivated or belongs to a deactivated company or workstation.":
    "Starfsmaður er óvirkur eða tilheyrir óvirku fyrirtæki eða starfsstöð.",
  "Deactivate company": "Gera fyrirtæki óvirkt",
  "Deactivate employee": "Gera starfsmann óvirkan",
  Deactivate: "Gera óvirkt",
  "Deactivating...": "Geri óvirkt...",
  "All workstations": "Allar starfsstöðvar",
  "Company deactivated.": "Fyrirtæki gert óvirkt.",
  "Employee deactivated.": "Starfsmaður gerður óvirkur.",
  "Workstation deactivated.": "Starfsstöð gerð óvirk.",
  "Connecting…": "Tengist…",
  "Waking up the server…": "Vek þjóninn…",
  "The server may be asleep. This can take up to two minutes.":
    "Þjónninn gæti verið í hvíld. Þetta getur tekið allt að tvær mínútur.",
  "Unable to connect": "Ekki tókst að tengjast",
  "Try again": "Reyna aftur",
  "Unable to connect to the server. Please try again.":
    "Ekki tókst að tengjast þjóninum. Reyndu aftur.",
  "The server is taking longer than expected. Please try again.":
    "Það tekur lengri tíma en búist var við að tengjast þjóninum. Reyndu aftur.",
  "Waking up the server… Your request will continue when it is ready.":
    "Vek þjóninn… Beiðnin heldur áfram þegar hann er tilbúinn.",
  "The server is unavailable. Your work is still here. Please try your action again.":
    "Þjónninn er ekki tiltækur. Vinnan þín er enn hér. Reyndu aðgerðina aftur.",
  "Checking your session...": "Athuga innskráningu...",
  "unknown user": "óþekktur notandi",
  "Failed to load current user.": "Mistókst að sækja núverandi notanda.",
  "Incapacity Records": "Fjarvistaskráning",
  "System Layout": "Kerfisyfirlit",
  "Sidebar navigation": "Hliðarvalmynd",
  "Main navigation": "Aðalvalmynd",
  Breadcrumb: "Brauðmolaslóð",
  Language: "Tungumál",
  "Choose language": "Veldu tungumál",
  "Log out": "Skrá út",
  "Checking role access...": "Athuga aðgangshlutverk...",
  "Unable to load role profile:": "Mistókst að sækja hlutverkaprófíl:",
  "Your role can browse, but only manager/admin users can edit client companies.":
    "Hlutverkið þitt getur skoðað, en aðeins stjórnendur geta breytt fyrirtæki.",
  "Only secretary/manager/admin users can create or edit employees and episodes.":
    "Aðeins ritarar og stjórnendur geta stofnað eða breytt starfsmönnum og fjarvistum.",

  Overview: "Yfirlit",
  "Logged-in system view": "Innskráð kerfissýn",
  "This root page is intentionally blank for now, with navigation on the left for the next modules.":
    "Þessi forsíða er vísvitandi tóm í bili, með leiðsögn vinstra megin fyrir næstu einingar.",
  Employees: "Starfsmenn",
  "Create episode": "Skrá fjarvist",
  Reports: "Skýrslur",
  "Follow-ups": "Eftirfylgni",
  "Client companies": "Fyrirtæki",
  New: "Nýtt",
  Create: "Stofna",
  Edit: "Breyta",
  Details: "Nánar",
  Refresh: "Endurnýja",
  Search: "Leita",
  "Page size": "Síðustærð",
  Previous: "Fyrri",
  Next: "Næsta",
  Save: "Vista",
  "Save changes": "Vista breytingar",
  Saving: "Vista",
  "Saving...": "Vista...",
  Cancel: "Hætta við",
  Add: "Bæta við",
  "Adding...": "Bæti við...",
  Delete: "Eyða",
  Loading: "Hleð",
  Actions: "Aðgerðir",
  Status: "Staða",
  Active: "Virkur",
  Name: "Nafn",
  Email: "Netfang",
  Phone: "Sími",
  Password: "Lykilorð",
  Login: "Innskráning",
  "Use your company account to continue.":
    "Notaðu fyrirtækjaaðganginn til að halda áfram.",
  "Continue with Google": "Halda áfram með Google",
  "Create account": "Stofna aðgang",
  "Base UI handles field and button primitives. Auth completes through Auth0 redirect.":
    "Base UI sér um reiti og hnappa. Auðkenning klárast með Auth0 tilvísun.",

  "Manage client company records and open edit forms.":
    "Sýslaðu með færslur fyrirtækja og opnaðu breytingaform.",
  "Loading companies...": "Hleð fyrirtækjum...",
  "No client companies found yet.": "Engin fyrirtæki fundust enn.",
  "Use the Create button above to add a client company.":
    "Notaðu Stofna hnappinn hér að ofan til að bæta við fyrirtæki.",
  "Ask a manager/admin to create the first client company.":
    "Biddu stjórnanda að stofna fyrsta fyrirtækið.",
  "Failed to load client companies.": "Mistókst að sækja fyrirtæki.",
  "Failed to load client companies": "Mistókst að sækja fyrirtæki",
  "Client company": "Fyrirtæki",
  "Client Company": "Fyrirtæki",
  "Company name": "Nafn fyrirtækis",
  "Registration Number": "Kennitala",
  "Contact Phone": "Sími tengiliðs",
  Address: "Heimilisfang",
  "Name is required": "Nafn er nauðsynlegt",
  "Registration number is required": "Kennitala er nauðsynleg",
  "Create client company": "Stofna fyrirtæki",
  "Edit client company": "Breyta fyrirtæki",
  Setup: "Uppsetning",
  "Creating...": "Stofna...",
  "Client company created.": "Fyrirtæki stofnað.",
  "Client company updated.": "Fyrirtæki uppfært.",
  "A client company with this national ID already exists.":
    "Fyrirtæki með þessari kennitölu er þegar til.",
  "A client company with this registration number already exists.":
    "Fyrirtæki með þessari kennitölu er þegar til.",
  "Only manager/admin users can create client companies.":
    "Aðeins stjórnendur geta stofnað fyrirtæki.",
  "Only manager/admin users can update client companies.":
    "Aðeins stjórnendur geta uppfært fyrirtæki.",
  "Missing client company ID in the route.":
    "Vantar auðkenni fyrirtækis í slóð.",
  "Client company was not found.": "Fyrirtæki fannst ekki.",
  "Failed to load client company.": "Mistókst að sækja fyrirtæki.",
  "Failed to update client company.": "Mistókst að uppfæra fyrirtæki.",
  "Failed to create client company.": "Mistókst að stofna fyrirtæki.",
  "You are not authorized to create client companies.":
    "Þú hefur ekki heimild til að stofna fyrirtæki.",
  "You are not authorized to update client companies.":
    "Þú hefur ekki heimild til að uppfæra fyrirtæki.",

  "Create and maintain employee records across client companies.":
    "Stofnaðu og viðhaldu starfsmannafærslum hjá fyrirtækjum.",
  "Employee filters": "Starfsmannasíur",
  Location: "Staðsetning",
  "Location is required": "Staðsetning er nauðsynleg",
  "Select location": "Veldu staðsetningu",
  "Name or national ID": "Nafn eða kennitala",
  "all companies": "öll fyrirtæki",
  "All companies": "Öll fyrirtæki",
  "Location type": "Tegund staðsetningar",
  "On land": "Í landi",
  "At sea": "Á sjó",
  "On land and at sea": "Í landi og á sjó",
  Employee: "Starfsmaður",
  View: "Skoða",
  "Customer details": "Upplýsingar um starfsmann",
  "Personal details": "Persónuupplýsingar",
  Placement: "Vinnustaður",
  Contact: "Samskipti",
  "Other details": "Aðrar upplýsingar",
  "Employee search": "Starfsmannaleit",
  "Employees table": "Starfsmannatafla",
  "Loading employees...": "Hleð starfsmönnum...",
  "No employees found for the selected filters.":
    "Engir starfsmenn fundust fyrir valdar síur.",
  "Use the Create button above to add an employee.":
    "Notaðu Stofna hnappinn hér að ofan til að bæta við starfsmanni.",
  "Ask a secretary/manager/admin to create employees.":
    "Biddu ritara eða stjórnanda að stofna starfsmenn.",
  "Failed to load employees.": "Mistókst að sækja starfsmenn.",
  "Create employee": "Stofna starfsmann",
  "Edit employee": "Breyta starfsmanni",
  "Employee created.": "Starfsmaður stofnaður.",
  "Employee updated.": "Starfsmaður uppfærður.",
  "Employee was not found.": "Starfsmaður fannst ekki.",
  "Missing employee ID in the route.": "Vantar auðkenni starfsmanns í slóð.",
  "Full name": "Fullt nafn",
  "First Name": "Fornafn",
  "First name": "Fornafn",
  "First name is required": "Fornafn er nauðsynlegt",
  "Last Name": "Eftirnafn",
  "Last name": "Eftirnafn",
  "Last name is required": "Eftirnafn er nauðsynlegt",
  "National ID": "Kennitala",
  "National ID is required": "Kennitala er nauðsynleg",
  "Employee number": "Starfsmannanúmer",
  "Client company is required": "Fyrirtæki er nauðsynlegt",
  Workstation: "Starfsstöð",
  "Workstation is required": "Starfsstöð er nauðsynleg",
  "Select client company": "Veldu fyrirtæki",
  "Select workstation": "Veldu starfsstöð",
  "Select client company first": "Veldu fyrirtæki fyrst",
  "No client company": "Ekkert fyrirtæki",
  "No workstation": "Engin starfsstöð",
  "Loading employee details...": "Hleð upplýsingum starfsmanns...",
  "An employee with this national ID already exists for the selected company.":
    "Starfsmaður með þessari kennitölu er þegar til hjá völdu fyrirtæki.",
  "You are not authorized to update employees.":
    "Þú hefur ekki heimild til að uppfæra starfsmenn.",
  "Only secretary/manager/admin users can update employees.":
    "Aðeins ritarar og stjórnendur geta uppfært starfsmenn.",

  Incapacity: "Fjarvist",
  "Create incapacity episode": "Stofna fjarvist",
  "Select an employee to review past episodes and start a new one.":
    "Veldu starfsmann til að skoða fyrri fjarvistir og stofna nýja.",
  "Select an employee to continue.": "Veldu starfsmann til að halda áfram.",
  "Find an employee": "Finna starfsmann",
  "Search for an employee to open their page and start an episode.":
    "Leitaðu að starfsmanni til að opna síðu hans og skrá fjarvist.",
  "Employee details": "Upplýsingar starfsmanns",
  "Ongoing episodes": "Fjarvistir í gangi",
  "No ongoing episodes.": "Engar fjarvistir í gangi.",
  "Failed to load recent episodes.": "Mistókst að sækja nýlegar fjarvistir.",
  "Back to search": "Til baka í leit",
  Open: "Opna",
  Note: "Athugasemd",
  "Job title": "Starfsheiti",
  Gender: "Kyn",
  Nationality: "Þjóðerni",
  Department: "Deild",
  Landline: "Heimasími",
  "Search by name or national ID.": "Leitaðu eftir nafni eða kennitölu.",
  "1. Employee lookup": "1. Starfsmannaleit",
  "2. Employee summary": "2. Samantekt starfsmanns",
  "3. Episodes": "3. Fjarvistir",
  "Clear selection": "Hreinsa val",
  "Search employees": "Leita í starfsmönnum",
  "Searching employees...": "Leita að starfsmönnum...",
  "No employees matched your search.": "Engir starfsmenn pössuðu við leitina.",
  "Employee search results": "Niðurstöður starfsmannaleitar",
  "Selected employee": "Valinn starfsmaður",
  "Recent episodes": "Nýlegar fjarvistir",
  "Start new episode": "Stofna nýja fjarvist",
  "Loading recent episodes...": "Hleð nýlegum fjarvistum...",
  "No previous episodes found.": "Engar fyrri fjarvistir fundust.",
  "Episodes for selected employee": "Fjarvistir valins starfsmanns",
  Episode: "Fjarvist",
  "Episode details": "Upplýsingar fjarvistar",
  "2. Episode details": "2. Upplýsingar fjarvistar",
  "Review and edit episode details, add follow-up tasks, and monitor existing tasks.":
    "Skoðaðu og breyttu fjarvist, bættu við eftirfylgniverkefnum og fylgstu með verkefnum.",
  "Review episode history or create a new episode.":
    "Skoðaðu sögu fjarvista eða stofnaðu nýja fjarvist.",
  "Back to intake": "Til baka í móttöku",
  "Loading episode details...": "Hleð upplýsingum fjarvistar...",
  "Episode ID": "Auðkenni fjarvistar",
  "Reported at": "Tilkynnt",
  "Start date": "Upphafsdagur",
  "Estimated return date": "Áætlaður endurkomudagur",
  "Estimated return date is required":
    "Áætlaður endurkomudagur er nauðsynlegur",
  "Est. return": "Áætl. endurk.",
  "Return date": "Endurkomudagur",
  Verified: "Staðfest",
  Yes: "Já",
  No: "Nei",
  "Reason code": "Ástæða",
  Reason: "Ástæða",
  "Initial comment": "Upphafsathugasemd",
  "Episode comments": "Athugasemdir við fjarvist",
  "New comment": "Ný athugasemd",
  "Add comment": "Bæta við athugasemd",
  "Comment added successfully.": "Athugasemd bætt við.",
  "Comment text is required.": "Athugasemd er nauðsynleg.",
  "No comments have been added yet.":
    "Engum athugasemdum hefur enn verið bætt við.",
  "Edit episode details": "Breyta upplýsingum fjarvistar",
  "Add follow-ups": "Bæta við eftirfylgni",
  "3. Follow-ups": "3. Eftirfylgni",
  "4. Submit": "4. Senda",
  "Add follow-up": "Bæta við eftirfylgni",
  "Add one or more follow-up tasks if needed.":
    "Bættu við einu eða fleiri eftirfylgniverkefnum ef þörf er á.",
  "Follow-up {{index}}": "Eftirfylgni {{index}}",
  "Remove follow-up {{index}}": "Fjarlægja eftirfylgni {{index}}",
  Remove: "Fjarlægja",
  "No follow-up tasks found.": "Engin eftirfylgniverkefni fundust.",
  "Episode follow-up tasks": "Eftirfylgniverkefni fjarvistar",
  "Episode updated successfully.": "Fjarvist uppfærð.",
  "Episode updated, but one or more follow-ups failed.":
    "Fjarvist var uppfærð, en eitt eða fleiri eftirfylgniverkefni mistókust.",
  "Episode not found.": "Fjarvist fannst ekki.",
  "Missing episode ID in route.": "Vantar auðkenni fjarvistar í slóð.",
  "You are not allowed to edit this episode.":
    "Þú mátt ekki breyta þessari fjarvist.",
  "You are not allowed to add comments to this episode.":
    "Þú mátt ekki bæta athugasemdum við þessa fjarvist.",
  "You are not authorized to edit episodes.":
    "Þú hefur ekki heimild til að breyta fjarvistum.",
  "You are not authorized to add comments.":
    "Þú hefur ekki heimild til að bæta við athugasemdum.",
  "Failed to load episode.": "Mistókst að sækja fjarvist.",
  "Failed to update episode.": "Mistókst að uppfæra fjarvist.",
  "Failed to add comment.": "Mistókst að bæta við athugasemd.",

  Illness: "Veikindi",
  "Work accident": "Vinnuslys",
  "Off the job accident": "Slys utan vinnu",
  "Visiting a doctor": "Læknisheimsókn",
  "Sick child": "Veikt barn",
  "Call employee": "Hringja í starfsmann",
  "Check in": "Skrá stöðu",
  "Request document": "Óska eftir skjali",
  "Check-up": "Skoðun",
  "Reason code is required": "Ástæða er nauðsynleg",
  "Reported at is required": "Tilkynningartími er nauðsynlegur",
  "Start date is required": "Upphafsdagur er nauðsynlegur",
  "Task type is required": "Tegund verkefnis er nauðsynleg",
  "Select task type": "Veldu tegund verkefnis",
  "Due date and time is required": "Skiladagur og tími eru nauðsynleg",
  "Comment cannot exceed 2000 characters":
    "Athugasemd má ekki vera lengri en 2000 stafir",
  "Do not enter unnecessary sensitive medical details.":
    "Ekki slá inn óþarfar viðkvæmar heilsufarsupplýsingar.",
  "{{count}}/2000 characters": "{{count}}/2000 stafir",
  "Select reason code": "Veldu ástæðu",
  "Follow-up tasks": "Eftirfylgniverkefni",
  "No follow-up task types are selected.":
    "Engar tegundir eftirfylgniverkefna eru valdar.",
  "Due date": "Skiladagur",
  "Due at": "Skilast",
  "Assigned to": "Úthlutað til",
  Description: "Lýsing",
  Task: "Verkefni",
  "Task type": "Tegund verkefnis",

  "Review open follow-up tasks by due date and update their status.":
    "Skoðaðu opin eftirfylgniverkefni eftir gjalddaga og uppfærðu stöðu þeirra.",
  "Pending & in progress": "Í bið og í vinnslu",
  "Failed to load follow-up tasks.": "Mistókst að sækja eftirfylgniverkefni.",
  "Failed to update follow-up task.":
    "Mistókst að uppfæra eftirfylgniverkefni.",
  "Follow-up filters": "Síur fyrir eftirfylgni",
  "Selected day": "Valinn dagur",
  "Follow-up tasks table": "Tafla eftirfylgniverkefna",
  "No follow-up tasks match the selected filters.":
    "Engin eftirfylgniverkefni passa við valdar síur.",
  pending: "í bið",
  "in progress": "í vinnslu",
  completed: "lokið",
  cancelled: "hætt við",
  "Updated follow-up status for": "Staða eftirfylgni uppfærð fyrir",
  "Failed to create follow-up {{index}} ({{status}}).":
    "Mistókst að stofna eftirfylgni {{index}} ({{status}}).",
  "Failed to create follow-up {{index}}.":
    "Mistókst að stofna eftirfylgni {{index}}.",
  "Follow-up {{index}}: {{reason}}": "Eftirfylgni {{index}}: {{reason}}",

  "Review incapacity episodes across client companies.":
    "Skoðaðu fjarvistir þvert á fyrirtæki.",
  "Report filters": "Skýrslusíur",
  "Date from": "Dagsetning frá",
  "Date to": "Dagsetning til",
  "All reasons": "Allar ástæður",
  "All statuses": "Allar stöður",
  "All locations": "Allar staðsetningar",
  Ongoing: "Í gangi",
  Upcoming: "Framundan",
  Ended: "Lokið",
  "Loading episodes...": "Hleð fjarvistum...",
  "Loading employee...": "Hleð starfsmanni...",
  "No episodes match the selected filters.":
    "Engar fjarvistir passa við valdar síur.",
  "Episodes report table": "Fjarvistaskýrsla",
  "Episodes report": "Fjarvistaskýrsla",
  "Browse episodes with server-side filters across companies and date windows.":
    "Skoðaðu fjarvistir eftir fyrirtæki, starfsstöð og tímabili.",
  "Episode filters": "Síur fjarvista",
  From: "Frá",
  To: "Til",
  "Dates include episodes where the employee was on leave at any point in the period, and episodes reported during the period.":
    "Tímabilið nær yfir fjarvistir þar sem starfsmaður var frá vinnu einhvern tíma á tímabilinu, auk fjarvista sem voru tilkynntar á tímabilinu.",
  "Days away": "Dagar frá",
  "Export PDF": "Sækja PDF",
  "Exporting...": "Útbý skýrslu...",
  "Select a client company to export": "Veldu fyrirtæki til að sækja skýrslu",
  "Failed to load report episodes.": "Mistókst að sækja fjarvistir í skýrslu.",
  "Failed to load report episodes ({{status}}).":
    "Mistókst að sækja fjarvistir í skýrslu ({{status}}).",
  "Failed to export report.": "Mistókst að útbúa skýrslu.",
  "The server did not return a PDF.": "Netþjónninn skilaði ekki PDF skjali.",
  "Failed to create export ({{status}}).":
    "Mistókst að útbúa skýrslu ({{status}}).",
  "Unable to load client-company filter options: {{error}}":
    "Ekki tókst að sækja fyrirtæki í síu: {{error}}",
  "No episodes found for {{company}}.":
    "Engar fjarvistir fundust fyrir {{company}}.",
  "{{count}} episode": "{{count}} fjarvist",
  "{{count}} episodes": "{{count}} fjarvistir",
  "Musculoskeletal issue": "Stoðkerfisvandi",
  "The incapacity is due to a musculoskeletal issue":
    "Óvinnufærnin stafar af stoðkerfisvanda",
  "Incapacity certified by Akkilles": "Óvinnufærni vottuð af Akkilles",
  "Incapacity has been certified by Akkilles":
    "Óvinnufærni hefur verið vottuð af Akkilles",
  Showing: "Sýni",
  of: "af",
  Pagination: "Síðuskipting",
  "Create new episode": "Stofna nýja fjarvist",
  "Open created episode": "Opna stofnaða fjarvist",
  "Episode summary": "Samantekt fjarvistar",
  Comments: "Athugasemdir",
  "Unknown author": "Óþekktur höfundur",
  "Failed to search employees ({{status}}).":
    "Mistókst að leita að starfsmönnum ({{status}}).",
  "Failed to load recent episodes ({{status}}).":
    "Mistókst að sækja nýlegar fjarvistir ({{status}}).",
  "Creating episode for {{name}}.": "Stofna fjarvist fyrir {{name}}.",
  "Creating episode for {{name}}": "Stofna fjarvist fyrir {{name}}",
  "Form actions": "Aðgerðir forms",
  "Missing employee ID in URL. Start from the episodes list.":
    "Vantar auðkenni starfsmanns í slóð. Byrjaðu á lista fjarvista.",
  "Employee not found.": "Starfsmaður fannst ekki.",
  "Failed to load employee ({{status}}).":
    "Mistókst að sækja starfsmann ({{status}}).",
  "Failed to load employee.": "Mistókst að sækja starfsmann.",
  "Only secretary/manager/admin users can create and edit episodes.":
    "Aðeins ritarar og stjórnendur geta stofnað og breytt fjarvistum.",
  "Select an employee before submitting.":
    "Veldu starfsmann áður en þú sendir inn.",
  "Episode was already created. Open the created episode to continue.":
    "Fjarvist hefur þegar verið stofnuð. Opnaðu stofnaða fjarvist til að halda áfram.",
  "You are not authorized to create episodes.":
    "Þú hefur ekki heimild til að stofna fjarvistir.",
  "The selected employee no longer exists.":
    "Valinn starfsmaður er ekki lengur til.",
  "The episode request is invalid.": "Beiðni um fjarvist er ógild.",
  "Created episode successfully.": "Fjarvist var stofnuð.",
  "Failed to create episode ({{status}}).":
    "Mistókst að stofna fjarvist ({{status}}).",
  "Load an employee to continue.": "Hladdu starfsmanni til að halda áfram.",
  "Complete episode details and add one or more follow-up tasks.":
    "Ljúktu við upplýsingar fjarvistar og bættu við einu eða fleiri eftirfylgniverkefnum.",
  "Failed to load episode ({{status}}).":
    "Mistókst að sækja fjarvist ({{status}}).",
  "Failed to update episode ({{status}}).":
    "Mistókst að uppfæra fjarvist ({{status}}).",
  "Failed to refresh episode ({{status}}).":
    "Mistókst að endurnýja fjarvist ({{status}}).",
  "Failed to add comment ({{status}}).":
    "Mistókst að bæta við athugasemd ({{status}}).",

  Workstations: "Starfsstöðvar",
  "Manage workstations for this client company.":
    "Sýslaðu með starfsstöðvar fyrir þetta fyrirtæki.",
  "Contact Email": "Netfang tengiliðs",
  "Loading workstations...": "Hleð starfsstöðvum...",
  "No workstations have been added yet.":
    "Engum starfsstöðvum hefur enn verið bætt við.",
  "Workstation added.": "Starfsstöð bætt við.",
  "Workstation updated.": "Starfsstöð uppfærð.",
  "Workstation deleted.": "Starfsstöð eytt.",
  "Failed to load workstations.": "Mistókst að sækja starfsstöðvar.",
  "Failed to save workstation.": "Mistókst að vista starfsstöð.",
  "Failed to delete workstation.": "Mistókst að eyða starfsstöð.",
  "A workstation with this name already exists for this company.":
    "Starfsstöð með þessu nafni er þegar til hjá fyrirtækinu.",
  "Enter a workstation name and, if given, a valid contact email.":
    "Sláðu inn nafn starfsstöðvar og, ef það er gefið upp, gilt netfang tengiliðs.",
  "Employees must be reassigned or removed before deleting this workstation.":
    "Endurúthluta eða fjarlægja þarf starfsmenn áður en starfsstöð er eytt.",
  "Only manager/admin users can manage workstations.":
    "Aðeins stjórnendur geta sýslað með starfsstöðvar.",

  Tag: "Merki",
  Tags: "Merki",
  "All tags": "Öll merki",
  "Workstation tags": "Merki starfsstöðva",
  "Group workstations with tags to filter reports by several workstations at once.":
    "Flokkaðu starfsstöðvar með merkjum til að sía skýrslur eftir mörgum starfsstöðvum í einu.",
  "Add tag": "Bæta við merki",
  "Rename tag": "Endurnefna merki",
  "Delete tag": "Eyða merki",
  Rename: "Endurnefna",
  "No tags have been added yet.": "Engum merkjum hefur enn verið bætt við.",
  "Tag added.": "Merki bætt við.",
  "Tag updated.": "Merki uppfært.",
  "Tag deleted.": "Merki eytt.",
  "Failed to load tags.": "Mistókst að sækja merki.",
  "Failed to save tag.": "Mistókst að vista merki.",
  "Failed to delete tag.": "Mistókst að eyða merki.",
  "A tag with this name already exists for this company.":
    "Merki með þessu nafni er þegar til hjá fyrirtækinu.",
  "Enter a tag name.": "Sláðu inn nafn merkis.",
  "The tag is removed from all workstations. Workstations and their employees are not affected.":
    "Merkið er fjarlægt af öllum starfsstöðvum. Starfsstöðvar og starfsmenn þeirra breytast ekki.",
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      is: { translation: isTranslations },
      en: { translation: {} },
    },
    lng: getInitialLanguage(),
    fallbackLng: fallbackLanguage,
    supportedLngs: supportedLanguages,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage"],
      lookupLocalStorage: languageStorageKey,
      caches: ["localStorage"],
    },
    returnEmptyString: false,
  });

export default i18n;
